import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronRight, FolderTree, ListChecks, Loader2, PackageOpen, Plus, RefreshCw, Tags, Undo2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { useSubpantalla } from '../ui/navegacionReact';
import { descendientes, mismoNombre, TIPOS_DE_DATO } from './catalogo.js';
import FichaProducto from './FichaProducto';
import GestionEtiquetas from './GestionEtiquetas';
import ExcelDeCategoria from './ExcelDeCategoria';

/* =====================================================================
   CATÁLOGO · CATÁLOGO VIVO F1 (Administración → Catálogo, R-CV7)
   =====================================================================
   Tres cosas en una pantalla, una por pestaña:
     · Categorías: el árbol, crear una dentro de otra, moverla con su rama,
       y qué datos pide cada una (los de sus madres llegan solos).
     · Plantillas listas: Libro, Componente electrónico, Tornillería… Se
       instalan sin productos; la empresa las edita después.
     · Fichas incompletas: productos a los que su categoría les pide algo
       que no tienen. Nada se bloquea ni se inventa: es la lista de pendientes.
     · Etiquetas (F4): color, juntar y archivar (GestionEtiquetas.jsx).
   ===================================================================== */
const PESTANAS = [
  { id: 'categorias', etiqueta: 'Categorías', Icono: FolderTree },
  { id: 'plantillas', etiqueta: 'Plantillas listas', Icono: PackageOpen },
  { id: 'incompletas', etiqueta: 'Fichas incompletas', Icono: ListChecks },
  { id: 'etiquetas', etiqueta: 'Etiquetas', Icono: Tags },
];
const mensaje = (e) => (e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');
const BOTON = 'min-h-11 inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-4';
const PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white disabled:opacity-60`;
const SECUNDARIO = `${BOTON} bg-white border border-gray-300 text-gray-800 disabled:opacity-60`;

function Estado({ hecho, error, onReintentar }) {
  return (
    <>
      {hecho && (
        <p className="text-sm text-green-800 inline-flex items-center gap-2" role="status">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />{hecho}
        </p>
      )}
      {error && (
        <div role="alert" className="text-sm text-red-700 space-y-2">
          <p>{error}</p>
          {onReintentar && (
            <button type="button" onClick={onReintentar} className={SECUNDARIO}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Volver a intentarlo
            </button>
          )}
        </div>
      )}
    </>
  );
}

function Cargando({ texto }) {
  return (
    <p className="text-sm text-gray-600 inline-flex items-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {texto}
    </p>
  );
}

/* ------------------------------------------------------------ Categorías */
function NuevaCategoria({ arbol, madre, onCreada }) {
  const [nombre, setNombre] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const limpio = nombre.replace(/\s+/g, ' ').trim();
  const repetida = mismoNombre(arbol.categorias, limpio);
  const motivo = !arbol.puede_crear ? 'Crear categorías lo hace un encargado, un administrador o el dueño.'
    : limpio.length < 2 ? 'Escribe al menos 2 letras.'
      : repetida ? `Ya existe «${repetida.nombre}» en ${repetida.ruta_texto}. Elígela en la lista o usa un nombre más preciso («${limpio} de ${madre ? madre.nombre.toLowerCase() : 'casa'}»).`
        : null;

  async function crear(e) {
    e.preventDefault();
    if (motivo) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post('/catalogo-vivo/catalogo/categorias', { nombre: limpio, id_padre: madre?.id ?? null });
      if (!r.creada) {
        setError(`Ya existe «${r.existente.nombre}» en ${r.existente.ruta_texto}. Usa otro nombre o elige ésa.`);
        return;
      }
      setNombre('');
      onCreada(r.categoria);
    } catch (err) {
      setError(mensaje(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={crear} className="rounded-xl border border-gray-200 p-3 space-y-2" data-catalogo-vivo="nueva-categoria">
      <label htmlFor="cv-nueva-categoria" className="block text-sm font-semibold text-gray-800">
        {madre ? `Nueva categoría dentro de «${madre.nombre}»` : 'Nueva categoría'}
      </label>
      <Ayuda id="catalogo_vivo.catalogo.nueva_categoria" />
      <input id="cv-nueva-categoria" value={nombre} maxLength={80} autoComplete="off"
        onChange={(e) => setNombre(e.target.value)} placeholder="Por ejemplo: Resistencia"
        className="w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base" />
      <button type="submit" disabled={Boolean(motivo) || enviando} className={`${PRIMARIO} w-full`}>
        {enviando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Plus className="h-5 w-5" aria-hidden="true" />}
        Crear categoría
      </button>
      {motivo && (limpio.length > 0 || !arbol.puede_crear) && (
        <p className="text-xs text-amber-800" data-motivo="nueva-categoria">{motivo}</p>
      )}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </form>
  );
}

function NuevoDato({ onCreado }) {
  const [etiqueta, setEtiqueta] = useState('');
  const [tipo, setTipo] = useState('TEXTO');
  const [unidad, setUnidad] = useState('');
  const [opciones, setOpciones] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const def = TIPOS_DE_DATO.find((t) => t.id === tipo);
  const lista = opciones.split(',').map((o) => o.trim()).filter(Boolean);
  const motivo = etiqueta.trim().length < 2 ? 'Escribe el nombre del dato.'
    : def.opciones && lista.length === 0 ? 'Escribe las opciones, separadas por coma.' : null;

  async function crear(e) {
    e.preventDefault();
    if (motivo) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post('/catalogo-vivo/catalogo/atributos', {
        etiqueta: etiqueta.trim(), tipo,
        unidad_base: def.unidad && unidad.trim() ? unidad.trim() : null,
        opciones: def.opciones ? lista : null,
        referencia_a: tipo === 'REFERENCIA' ? 'producto' : null,
      });
      setEtiqueta(''); setUnidad(''); setOpciones('');
      onCreado(r.atributo);
    } catch (err) {
      setError(mensaje(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={crear} className="rounded-lg bg-gray-50 p-3 space-y-2" data-catalogo-vivo="nuevo-dato">
      <p className="text-sm font-semibold text-gray-800">Un dato nuevo</p>
      <Ayuda id="catalogo_vivo.catalogo.nuevo_dato" />
      <label className="block text-sm text-gray-700">Nombre
        <input value={etiqueta} maxLength={80} onChange={(e) => setEtiqueta(e.target.value)}
          placeholder="Por ejemplo: Rosca" className="mt-1 w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base" />
      </label>
      <label className="block text-sm text-gray-700">Qué se escribe
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}
          className="mt-1 w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base bg-white">
          {TIPOS_DE_DATO.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
      </label>
      {def.unidad && (
        <label className="block text-sm text-gray-700">Unidad (opcional)
          <input value={unidad} maxLength={12} onChange={(e) => setUnidad(e.target.value)}
            placeholder="Ω, V, mm, g…" className="mt-1 w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base" />
        </label>
      )}
      {def.opciones && (
        <label className="block text-sm text-gray-700">Opciones, separadas por coma
          <input value={opciones} onChange={(e) => setOpciones(e.target.value)}
            placeholder="Chica, Mediana, Grande" className="mt-1 w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base" />
        </label>
      )}
      <button type="submit" disabled={Boolean(motivo) || enviando} className={`${SECUNDARIO} w-full`}>
        {enviando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Plus className="h-5 w-5" aria-hidden="true" />}
        Crear el dato y ponerlo aquí
      </button>
      {motivo && etiqueta.length > 0 && <p className="text-xs text-amber-800">{motivo}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </form>
  );
}

function DetalleCategoria({ categoria, arbol, onCambio }) {
  const [plantilla, setPlantilla] = useState(null);
  const [definiciones, setDefiniciones] = useState([]);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [quitado, setQuitado] = useState(null);
  const [elegido, setElegido] = useState('');
  const puede = arbol.puede_plantillas;

  const cargar = useCallback(() => {
    Promise.all([api.get(`/catalogo-vivo/catalogo/categorias/${categoria.id}/plantilla`),
      api.get('/catalogo-vivo/catalogo/atributos')])
      .then(([p, d]) => { setError(null); setPlantilla(p.atributos); setDefiniciones(d.atributos); })
      .catch((e) => setError(mensaje(e)));
  }, [categoria.id]);
  // Cada categoría monta su propio detalle (`key`), así que no hay estado viejo que limpiar.
  useEffect(() => { cargar(); }, [cargar]);

  async function poner(cuerpo, aviso) {
    setError(null);
    try {
      const r = await api.put(`/catalogo-vivo/catalogo/categorias/${categoria.id}/atributos`, cuerpo);
      setPlantilla(r.atributos);
      setHecho(aviso);
      onCambio();
    } catch (e) {
      setError(mensaje(e));
    }
  }

  async function mover(idPadre) {
    setError(null);
    try {
      await api.patch(`/catalogo-vivo/catalogo/categorias/${categoria.id}/lugar`,
        idPadre === '' ? { a_la_raiz: true } : { id_padre: Number(idPadre) });
      setHecho('Listo: la categoría y todo lo que tiene dentro quedaron en su nuevo lugar.');
      onCambio();
    } catch (e) {
      setError(mensaje(e));
    }
  }

  const fuera = descendientes(arbol.categorias, categoria.id);
  const madres = arbol.categorias.filter((c) => !fuera.has(c.id) && c.profundidad < arbol.profundidad_maxima);
  const yaEstan = new Set((plantilla || []).map((a) => a.id_campo));
  const disponibles = definiciones.filter((d) => !yaEstan.has(d.id));
  const motivoSinPermiso = 'Decidir qué datos pide cada categoría lo hace el dueño o quien tenga el permiso «Armar las plantillas del catálogo».';

  return (
    <section className="rounded-xl border border-gray-200 p-3 space-y-3" data-catalogo-vivo="detalle-categoria">
      <div>
        <h3 className="font-semibold text-gray-900 break-words">{categoria.nombre}</h3>
        <p className="text-xs text-gray-600 break-words">{categoria.ruta_texto} · {categoria.productos} producto(s)</p>
      </div>
      <Estado hecho={hecho} error={error} />
      {quitado && (
        <button type="button" className={SECUNDARIO} onClick={() => { poner({ ...quitado, activo: true }, 'Listo: el dato volvió a esta categoría.'); setQuitado(null); }}>
          <Undo2 className="h-4 w-4" aria-hidden="true" /> Deshacer: volver a poner el dato
        </button>
      )}
      <label className="block text-sm text-gray-700">Va dentro de
        <select value={categoria.id_padre ?? ''} disabled={!arbol.puede_crear} onChange={(e) => mover(e.target.value)}
          className="mt-1 w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base bg-white">
          <option value="">(ninguna: es una categoría principal)</option>
          {madres.map((c) => <option key={c.id} value={c.id}>{c.ruta_texto}</option>)}
        </select>
      </label>
      {!arbol.puede_crear && <p className="text-xs text-gray-600">Mover categorías lo hace un encargado, un administrador o el dueño.</p>}
      <Ayuda id="catalogo_vivo.catalogo.madre" />

      <h4 className="text-sm font-semibold text-gray-800">Datos que pide su ficha</h4>
      <Ayuda id="catalogo_vivo.catalogo.datos" />
      {plantilla === null && !error && <Cargando texto="Leyendo la plantilla…" />}
      {plantilla?.length === 0 && (
        <p className="text-sm text-gray-600 rounded-lg border border-gray-100 p-2">
          Esta categoría solo pide lo de siempre (nombre, código, cantidad, ubicación…). Añade abajo los datos propios, como «Talla» o «Voltaje».
        </p>
      )}
      <ul className="space-y-1">
        {plantilla?.map((a) => (
          <li key={a.id_campo} className="rounded-lg border border-gray-100 p-2 text-sm" data-atributo={a.clave}>
            <span className="block font-semibold text-gray-900">{a.nombre}{a.unidad_base ? ` (${a.unidad_base})` : ''}</span>
            <span className="block text-xs text-gray-600">
              {TIPOS_DE_DATO.find((t) => t.id === a.tipo)?.nombre || a.tipo}
              {a.heredado_de ? ` · viene de «${arbol.categorias.find((c) => c.id === a.heredado_de)?.nombre || 'su madre'}»` : ''}
              {a.obligatorio ? ' · obligatorio' : ''}
            </span>
            {!a.heredado_de && (
              <span className="mt-1 flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 min-h-11">
                  <input type="checkbox" className="h-5 w-5" checked={a.obligatorio} disabled={!puede}
                    onChange={(e) => poner({ id_campo: a.id_campo, obligatorio: e.target.checked, etiqueta_local: a.nombre !== a.etiqueta ? a.nombre : null },
                      e.target.checked ? `Listo: «${a.nombre}» ahora es obligatorio.` : `Listo: «${a.nombre}» ya no es obligatorio.`)} />
                  Obligatorio
                </label>
                <button type="button" disabled={!puede} className={SECUNDARIO}
                  onClick={() => { setQuitado({ id_campo: a.id_campo, obligatorio: a.obligatorio }); poner({ id_campo: a.id_campo, activo: false }, `Listo: «${a.nombre}» ya no se pide en esta categoría. Lo que ya tenían los productos se conserva.`); }}>
                  Quitar de esta categoría
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      <Ayuda id="catalogo_vivo.catalogo.obligatorio" />
      {puede ? (
        <>
          {disponibles.length > 0 && (
            <div className="flex flex-col gap-2">
              <label className="block text-sm text-gray-700">Añadir un dato que ya existe
                <select value={elegido} onChange={(e) => setElegido(e.target.value)}
                  className="mt-1 w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base bg-white">
                  <option value="">Elige un dato…</option>
                  {disponibles.map((d) => <option key={d.id} value={d.id}>{d.etiqueta}{d.unidad_base ? ` (${d.unidad_base})` : ''}</option>)}
                </select>
              </label>
              <button type="button" disabled={!elegido} className={SECUNDARIO}
                onClick={() => { poner({ id_campo: Number(elegido), orden: 100 + (plantilla?.length || 0) * 10 }, 'Listo: el dato ya se pide en esta categoría y en las que tiene dentro.'); setElegido(''); }}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Añadir
              </button>
              {!elegido && <p className="text-xs text-gray-600">Elige un dato de la lista para añadirlo.</p>}
            </div>
          )}
          <NuevoDato onCreado={(d) => poner({ id_campo: d.id, orden: 100 + (plantilla?.length || 0) * 10 }, `Listo: «${d.etiqueta}» ya se pide en esta categoría.`)} />
        </>
      ) : (
        <p className="text-xs text-gray-600">{motivoSinPermiso}</p>
      )}
      <ExcelDeCategoria categoria={categoria} puedeImportar={Boolean(arbol.puede_importar)} />
    </section>
  );
}

function Categorias({ irA }) {
  const [arbol, setArbol] = useState(null);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [abierta, setAbierta] = useState(null);
  // Abrir una categoría es una subpantalla: atrás la cierra, no sale de Catálogo.
  useSubpantalla('catalogo-categoria', abierta !== null, () => setAbierta(null), { valor: abierta, abrir: setAbierta });

  const cargar = useCallback(() => {
    api.get('/catalogo-vivo/catalogo/categorias')
      .then((r) => { setError(null); setArbol(r); })
      .catch((e) => setError(mensaje(e)));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const actual = useMemo(() => arbol?.categorias.find((c) => c.id === abierta) || null, [arbol, abierta]);

  if (error && !arbol) return <Estado error={error} onReintentar={() => { setError(null); cargar(); }} />;
  if (!arbol) return <Cargando texto="Leyendo tus categorías…" />;
  return (
    <div className="space-y-3">
      <Estado hecho={hecho} error={error} />
      {arbol.categorias.length === 0 ? (
        <div className="rounded-xl border border-gray-200 p-3 space-y-2" data-catalogo-vivo="sin-categorias">
          <p className="text-sm text-gray-700">No hay categorías. Agrupan productos y deciden qué pide la ficha de cada uno: el autor de un libro, el valor de una resistencia.</p>
          <button type="button" className={PRIMARIO} onClick={() => irA('plantillas')}>
            <PackageOpen className="h-5 w-5" aria-hidden="true" /> Ver plantillas listas
          </button>
        </div>
      ) : (
        <ul className="space-y-1" data-catalogo-vivo="arbol">
          {arbol.categorias.map((c) => (
            <li key={c.id} data-categoria={c.id} style={{ paddingLeft: `${(c.profundidad - 1) * 16}px` }}>
              <button type="button" onClick={() => setAbierta(c.id === abierta ? null : c.id)}
                aria-expanded={c.id === abierta}
                className={`w-full min-h-11 flex items-center gap-2 rounded-lg border px-3 text-left text-sm ${c.id === abierta ? 'border-[var(--color-marca)] bg-gray-50' : 'border-gray-100'}`}>
                <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" aria-hidden="true" />
                <span className="flex-1 min-w-0 break-words font-semibold text-gray-900">{c.nombre}</span>
                <span className="text-xs text-gray-600 shrink-0">{c.productos} productos · {c.atributos_propios} datos</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {actual && <DetalleCategoria key={actual.id} categoria={actual} arbol={arbol} onCambio={cargar} />}
      <NuevaCategoria arbol={arbol} madre={actual}
        onCreada={(c) => { setHecho(`Listo: «${c.nombre}» quedó en ${c.ruta_texto}.`); setAbierta(c.id); cargar(); }} />
    </div>
  );
}

/* ------------------------------------------------------ Plantillas listas */
function Plantillas({ irA }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [instalando, setInstalando] = useState(null);

  const cargar = useCallback(() => {
    api.get('/catalogo-vivo/catalogo/plantillas')
      .then((r) => { setError(null); setDatos(r); })
      .catch((e) => setError(mensaje(e)));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function instalar(clave, nombre) {
    setInstalando(clave);
    setError(null);
    setHecho(null);
    try {
      const r = await api.post(clave === 'casa' ? '/catalogo-vivo/catalogo/plantillas-casa' : `/catalogo-vivo/catalogo/plantillas/${clave}`);
      const nuevas = r.categorias_creadas.length;
      setHecho(nuevas > 0
        ? `Listo: «${nombre}» quedó instalada con ${nuevas} categoría(s). Ya puedes usarla en tus productos.`
        : `«${nombre}» ya estaba instalada; se completó lo que faltaba sin tocar lo que ya editaste.`);
      cargar();
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setInstalando(null);
    }
  }

  if (error && !datos) return <Estado error={error} onReintentar={() => { setError(null); cargar(); }} />;
  if (!datos) return <Cargando texto="Leyendo las plantillas…" />;
  const motivo = 'Instalar plantillas lo hace el dueño o quien tenga el permiso «Armar las plantillas del catálogo».';
  return (
    <div className="space-y-3">
      <Ayuda id="catalogo_vivo.catalogo.instalar" />
      <Estado hecho={hecho} error={error} />
      {hecho && (
        <button type="button" className={SECUNDARIO} onClick={() => irA('categorias')}>
          <FolderTree className="h-4 w-4" aria-hidden="true" /> Ver mis categorías
        </button>
      )}
      {!datos.puede_instalar && <p className="text-xs text-gray-600">{motivo}</p>}
      {datos.cuenta_personal && (
        <section className="rounded-xl border border-gray-200 p-3 space-y-2" data-plantilla="casa">
          <h3 className="font-semibold text-gray-900">Las de casa</h3>
          <p className="text-sm text-gray-600">{datos.casa.join(', ')}.</p>
          <button type="button" disabled={!datos.puede_instalar || instalando !== null}
            onClick={() => instalar('casa', 'Las de casa')} className={`${PRIMARIO} w-full`}>
            {instalando === 'casa' ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <PackageOpen className="h-5 w-5" aria-hidden="true" />}
            Instalar las de casa
          </button>
        </section>
      )}
      {datos.plantillas.map((p) => (
        <section key={p.clave} className="rounded-xl border border-gray-200 p-3 space-y-2" data-plantilla={p.clave}>
          <h3 className="font-semibold text-gray-900 break-words">{p.nombre}</h3>
          {p.atributos.length > 0 && <p className="text-sm text-gray-600 break-words">Pide: {p.atributos.join(', ')}.</p>}
          {p.hijas.length > 0 && <p className="text-xs text-gray-600 break-words">Trae dentro: {p.hijas.map((h) => h.nombre).join(', ')}.</p>}
          {p.instalada && (
            <p className="text-sm text-green-800 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Instalada</p>
          )}
          <button type="button" disabled={!datos.puede_instalar || instalando !== null}
            onClick={() => instalar(p.clave, p.nombre)} className={`${p.instalada ? SECUNDARIO : PRIMARIO} w-full`}>
            {instalando === p.clave ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <PackageOpen className="h-5 w-5" aria-hidden="true" />}
            {p.instalada ? 'Completar lo que falte' : 'Instalar'}
          </button>
        </section>
      ))}
    </div>
  );
}

/* ----------------------------------------------------- Fichas incompletas */
function Incompletas({ irA }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [abierto, setAbierto] = useState(null);
  useSubpantalla('catalogo-ficha', abierto !== null, () => setAbierto(null), { valor: abierto, abrir: setAbierto });
  const cargar = useCallback(() => {
    api.get('/catalogo-vivo/catalogo/fichas-incompletas')
      .then((r) => { setError(null); setDatos(r); })
      .catch((e) => setError(mensaje(e)));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  if (error) return <Estado error={error} onReintentar={() => { setError(null); cargar(); }} />;
  if (!datos) return <Cargando texto="Buscando fichas incompletas…" />;
  return (
    <div className="space-y-3">
      <Ayuda id="catalogo_vivo.catalogo.incompletas" />
      {datos.productos.length === 0 ? (
        <div className="rounded-xl border border-gray-200 p-3 space-y-2" data-catalogo-vivo="sin-incompletas">
          <p className="text-sm text-gray-700">Todas las fichas tienen lo que su categoría pide. Cuando una categoría reciba datos nuevos, sus productos aparecerán aquí para completarlos.</p>
          <button type="button" className={SECUNDARIO} onClick={() => irA('categorias')}>
            <FolderTree className="h-4 w-4" aria-hidden="true" /> Ver mis categorías
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-700">{datos.total} producto(s) con datos por llenar.</p>
          <ul className="space-y-1">
            {datos.productos.map((p) => (
              <li key={p.id} className="rounded-lg border border-gray-100 p-2 text-sm" data-incompleta={p.id}>
                <span className="block font-semibold text-gray-900 break-words">{p.nombre} <span className="font-mono text-xs text-gray-500">{p.sku}</span></span>
                <span className="block text-xs text-gray-600 break-words">{p.categoria} · Falta: {p.que_falta.join(', ')}</span>
                {p.faltan_obligatorios > 0 && (
                  <span className="block text-xs text-amber-800">{p.faltan_obligatorios} obligatorio(s)</span>
                )}
                {abierto === p.id ? (
                  <div className="mt-2">
                    <FichaProducto idProducto={p.id} onCerrar={() => setAbierto(null)} onGuardado={cargar} />
                  </div>
                ) : (
                  <button type="button" onClick={() => setAbierto(p.id)} className={`${SECUNDARIO} mt-2 w-full`}>
                    Completar la ficha
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function Catalogo() {
  const [pestana, setPestana] = useState('categorias');
  // Las pestañas que no son la base van al historial: atrás vuelve a Categorías.
  useSubpantalla('catalogo-pestana', pestana !== 'categorias', () => setPestana('categorias'),
    { valor: pestana, abrir: setPestana, reemplaza: true });
  return (
    <div className="space-y-4" data-catalogo-vivo="catalogo">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Catálogo</h2>
        <p className="text-sm text-gray-500 mt-0.5">Las categorías de tus productos y los datos que pide la ficha de cada una.</p>
      </div>
      <Ayuda id="catalogo_vivo.catalogo.pantalla" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1" role="tablist">
        {PESTANAS.map(({ id, etiqueta, Icono }) => (
          <button key={id} type="button" role="tab" aria-selected={pestana === id} onClick={() => setPestana(id)}
            className={`min-h-12 flex flex-col items-center justify-center gap-0.5 rounded-xl border px-1 text-xs font-semibold ${pestana === id ? 'border-[var(--color-marca)] text-[var(--color-marca)] bg-gray-50' : 'border-gray-200 text-gray-700'}`}>
            <Icono className="h-4 w-4" aria-hidden="true" />{etiqueta}
          </button>
        ))}
      </div>
      {pestana === 'categorias' && <Categorias irA={setPestana} />}
      {pestana === 'plantillas' && <Plantillas irA={setPestana} />}
      {pestana === 'incompletas' && <Incompletas irA={setPestana} />}
      {pestana === 'etiquetas' && <GestionEtiquetas />}
    </div>
  );
}
